import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * MS555 — read/write hooks for the Settings → Tiers panel.
 *
 * Tables (landed in MS553, /supabase/migrations/20260528020000_ms553_*.sql):
 *   - company_celebration_tiers — buyer-defined custom tiers with their
 *     own per-celebration ceilings. `name` is free-text (Lucky's lock
 *     per MS552 §3) and `properties_jsonb` is the forward-compat sidecar
 *     intentionally left empty for v1.
 *   - team_member_tier_assignments — join with UNIQUE(team_member_id);
 *     the constraint is the schema-level backstop for the "one employee
 *     can't be in multiple tiers" rule. Picker-time dedupe in the UI
 *     is the primary gate (MS552 §3).
 *
 * The supabase types in src/integrations/supabase/types.ts have not yet
 * been regenerated against MS553; calls use `from(... as never)` casts
 * to satisfy the typed client without regenerating the global file.
 * Same pattern that MS571 deferred for chat_sessions.is_active_draft.
 */

export interface CelebrationTier {
  id: string;
  company_id: string;
  name: string;
  per_celebration_ceiling_cents: number;
  /** Forward-compat sidecar — empty for v1 per MS555 step 4. */
  properties_jsonb: Record<string, unknown>;
  created_at: string;
}

/**
 * A tier row enriched with its assigned-member count. The count comes
 * from a separate aggregate query rather than embedded under
 * team_member_tier_assignments because PostgREST's nested count needs
 * an FK relationship name the codebase isn't using yet — a parallel
 * SELECT is cheap and avoids a relation hint.
 */
export interface CelebrationTierWithCount extends CelebrationTier {
  member_count: number;
}

export interface TierAssignment {
  id: string;
  team_member_id: string;
  tier_id: string;
  created_at: string;
}

/**
 * List every tier for the company plus a member_count rollup. Used by
 * the Settings → Tiers panel overview cards.
 */
export function useCelebrationTiers(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["celebration_tiers", companyId, "with-counts"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<CelebrationTierWithCount[]> => {
      if (!companyId) return [];
      const { data: tiers, error: tiersError } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("company_celebration_tiers" as any)
        .select("id, company_id, name, per_celebration_ceiling_cents, properties_jsonb, created_at")
        .eq("company_id", companyId)
        .order("created_at", { ascending: true });
      if (tiersError) throw tiersError;
      // Cast through unknown — the supabase-js typed client can't infer
      // the row shape for the `as any` table name (types haven't been
      // regenerated post-MS553). The select projection is exhaustive
      // and matches CelebrationTier exactly.
      const tierRows = (tiers as unknown as CelebrationTier[] | null) ?? [];
      if (tierRows.length === 0) return [];

      const tierIds = tierRows.map((t) => t.id);
      // PostgREST returns a row per assignment; we aggregate client-side.
      // For realistic team sizes (≤500 per useTeamMembers) this is cheap
      // and avoids relying on an RPC.
      const { data: assignments, error: assignmentsError } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("team_member_tier_assignments" as any)
        .select("tier_id")
        .in("tier_id", tierIds);
      if (assignmentsError) throw assignmentsError;
      const counts = new Map<string, number>();
      for (const row of (assignments as unknown as { tier_id: string }[] | null) ?? []) {
        counts.set(row.tier_id, (counts.get(row.tier_id) ?? 0) + 1);
      }
      return tierRows.map((t) => ({
        ...t,
        member_count: counts.get(t.id) ?? 0,
      }));
    },
  });
}

/**
 * All current tier assignments for the company. Joins through tiers so
 * RLS scopes by company (the assignments table's RLS predicate joins
 * via tier.company_id, see MS553 RLS section). Used by the employee
 * picker to know "who is in which tier" so the inline "move?"
 * affordance can fire without per-row preflight round-trips.
 */
export interface TierAssignmentWithTeamMember extends TierAssignment {
  tier_name: string;
}

export function useAllTierAssignments(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["tier_assignments", companyId, "all"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<TierAssignmentWithTeamMember[]> => {
      if (!companyId) return [];
      // We join tiers so we can return the tier name alongside the
      // assignment. The RLS policy on team_member_tier_assignments
      // already restricts visibility to the caller's company via the
      // tier join.
      const { data: tiers, error: tiersError } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("company_celebration_tiers" as any)
        .select("id, name")
        .eq("company_id", companyId);
      if (tiersError) throw tiersError;
      const tierRows = (tiers as unknown as { id: string; name: string }[] | null) ?? [];
      if (tierRows.length === 0) return [];
      const tierIds = tierRows.map((t) => t.id);
      const nameById = new Map(tierRows.map((t) => [t.id, t.name]));

      const { data: assignments, error: assignmentsError } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("team_member_tier_assignments" as any)
        .select("id, team_member_id, tier_id, created_at")
        .in("tier_id", tierIds);
      if (assignmentsError) throw assignmentsError;
      const rows = (assignments as unknown as TierAssignment[] | null) ?? [];
      return rows.map((a) => ({
        ...a,
        tier_name: nameById.get(a.tier_id) ?? "",
      }));
    },
  });
}

export interface CreateTierInput {
  companyId: string;
  name: string;
  perCelebrationCeilingCents: number;
}

export interface UpdateTierInput {
  tierId: string;
  name: string;
  perCelebrationCeilingCents: number;
}

/**
 * Centralised invalidation across the tier hooks so callers don't have
 * to know the key shapes. Mutating any of (tiers, assignments) bumps
 * both query trees because the Settings panel renders the joined view.
 */
function invalidateTierCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  companyId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: ["celebration_tiers", companyId],
  });
  void queryClient.invalidateQueries({
    queryKey: ["tier_assignments", companyId],
  });
}

export function useCreateTier(companyId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTierInput): Promise<CelebrationTier> => {
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("company_celebration_tiers" as any)
        .insert({
          company_id: input.companyId,
          name: input.name,
          per_celebration_ceiling_cents: input.perCelebrationCeilingCents,
          properties_jsonb: {},
        })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as CelebrationTier;
    },
    onSuccess: () => {
      if (companyId) invalidateTierCaches(queryClient, companyId);
    },
  });
}

export function useUpdateTier(companyId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateTierInput): Promise<CelebrationTier> => {
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("company_celebration_tiers" as any)
        .update({
          name: input.name,
          per_celebration_ceiling_cents: input.perCelebrationCeilingCents,
        })
        .eq("id", input.tierId)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as CelebrationTier;
    },
    onSuccess: () => {
      if (companyId) invalidateTierCaches(queryClient, companyId);
    },
  });
}

export function useDeleteTier(companyId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tierId: string): Promise<void> => {
      // ON DELETE CASCADE on team_member_tier_assignments.tier_id (see
      // MS553 migration line 197) removes every assignment row for the
      // tier automatically. Affected employees fall back to the
      // company-wide per_celebration_ceiling_cents — the deletion is a
      // single statement and the cascade is the load-bearing cleanup.
      const { error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("company_celebration_tiers" as any)
        .delete()
        .eq("id", tierId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (companyId) invalidateTierCaches(queryClient, companyId);
    },
  });
}

/**
 * Apply a batch of picker changes atomically (best-effort sequential).
 *
 * `moves` are assignments where the team member is already in a
 * different tier — we DELETE the old row then INSERT the new one. The
 * DB UNIQUE(team_member_id) is the backstop; the inline picker UI
 * gates this at confirm time per MS552 §3 so the buyer never sees a
 * raw 23505.
 *
 * `adds` are assignments where the team member is currently unassigned;
 * a single INSERT is enough.
 *
 * `removes` are unassign operations from chips the buyer X'd out
 * before saving — straight DELETEs by assignment id.
 *
 * No RPC for v1 — the count is small (one save = one tier's worth of
 * member edits) and the failure mode (partial save) surfaces as a
 * toast so the buyer can retry. Atomicity via RPC is a future cleanup
 * if usage grows.
 */
export interface ApplyAssignmentChangesInput {
  tierId: string;
  /** team_member_id → existing assignment id in the other tier. */
  moves: Array<{ teamMemberId: string; previousAssignmentId: string }>;
  /** team_member_ids with no current assignment. */
  adds: string[];
  /** Existing assignment ids in *this* tier that the buyer un-chipped. */
  removes: string[];
}

export function useApplyTierAssignmentChanges(
  companyId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ApplyAssignmentChangesInput): Promise<void> => {
      // Order matters: removes + delete-of-moves first, then inserts.
      // Otherwise an INSERT for a moved member would race the UNIQUE
      // constraint against the still-present old assignment row.
      if (input.removes.length > 0) {
        const { error } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from("team_member_tier_assignments" as any)
          .delete()
          .in("id", input.removes);
        if (error) throw error;
      }
      if (input.moves.length > 0) {
        const previousIds = input.moves.map((m) => m.previousAssignmentId);
        const { error } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from("team_member_tier_assignments" as any)
          .delete()
          .in("id", previousIds);
        if (error) throw error;
      }
      const newAssignments = [
        ...input.adds.map((teamMemberId) => ({
          team_member_id: teamMemberId,
          tier_id: input.tierId,
        })),
        ...input.moves.map(({ teamMemberId }) => ({
          team_member_id: teamMemberId,
          tier_id: input.tierId,
        })),
      ];
      if (newAssignments.length > 0) {
        const { error } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from("team_member_tier_assignments" as any)
          .insert(newAssignments);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      if (companyId) invalidateTierCaches(queryClient, companyId);
    },
  });
}
